"use client";
import type { AuthState } from "@jetblue/kit-auth";
import type { ReactNode } from "react";
import { useMemo } from "react";
import { DotcomAuthContext } from "../context";
import { useFetchProfile } from "./hooks/use-fetch-profile";
import type { DotcomAuthSession, KitAuthConfig } from "../types";
import { useKitAuth } from "./hooks/use-kit-auth";

export type KitAuthSessionProperties = {
  authConfig: KitAuthConfig;
  children: ReactNode;
};
export const KitAuthSession = ({
  authConfig,
  children,
}: KitAuthSessionProperties) => {
  const {
    isAuthenticated,
    accessToken,
    error,
    isLoading: isLoadingKitAuth,
    handleLogout,
    handleLogin,
  } = useKitAuth();
  const {
    data: profile,
    error: profileError,
    isLoading: isLoadingProfile,
  } = useFetchProfile({
    accessToken,
    enabled: Boolean(accessToken),
    url: authConfig.PROFILE_URL,
  });

  const value = useMemo<DotcomAuthSession>(() => {
    let status: AuthState;
    if (!isAuthenticated && !isLoadingKitAuth && !isLoadingProfile) {
      status = "unauthenticated";
    } else if (profile) {
      status = "authenticated";
    } else if (error) {
      status = "unauthenticated";
    } else {
      status = "authenticating";
    }

    return {
      accessToken,
      customerDetails: profile,
      error,
      handleLogin,
      handleLogout,
      status,
    };
  }, [
    accessToken,
    isAuthenticated,
    profile,
    profileError,
    isLoadingKitAuth,
    isLoadingProfile,
  ]);

  return (
    <DotcomAuthContext.Provider value={value}>
      {children}
    </DotcomAuthContext.Provider>
  );
};
